import React from 'react';
import AddIcon from '@mui/icons-material/Add';
import { FieldArray } from 'formik';
import { isEmpty } from 'lodash';

import { FsButton } from '~/components/core';
import IconButton from '~/components/core/Atomic/Buttons/IconButton';
import Text from '~/components/core/TextComponents/Text';
import { TrashIcon } from '~/components/icons';
import { FORM_IDS } from '~/components/SystemConfiguration/Communications/PhoneConfigurationTab/PhoneConfigurations/PhoneSurveyConfiguration/utils/constants';
import { getEmptyOption } from '~/components/SystemConfiguration/Communications/PhoneConfigurationTab/PhoneConfigurations/PhoneSurveyConfiguration/utils/helpers';
import TextFieldFormik from '~/components/TextFieldFormik';
import type {
  PhoneSurveyConfigurationModelPhoneSurveyAnswerOptionConfigurationModel,
  PhoneSurveyConfigurationModelPhoneSurveyQuestionConfigurationModel,
} from '~/server_shared/openapi/data-contracts';

interface OptionsConfigurationProps {
  question: PhoneSurveyConfigurationModelPhoneSurveyQuestionConfigurationModel;
  questionIndex: number;
  setFieldValue: (field: string, value: unknown) => void;
}

const OptionsConfiguration: React.FC<OptionsConfigurationProps> = ({ question, questionIndex, setFieldValue }) => {
  return (
    <div className="rounded-lg bg-slate-100 px-16 py-12">
      <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.SEMI_BOLD}>
        Options
      </Text>
      <FieldArray name={`${FORM_IDS.QUESTIONS}.${questionIndex}.answer_options`}>
        {({ remove: removeOption, push: pushOption }) => (
          <>
            {question?.answer_options?.map(
              (option: PhoneSurveyConfigurationModelPhoneSurveyAnswerOptionConfigurationModel, optionIndex: number) => (
                <div key={optionIndex} className="flex items-center py-12">
                  <div className="flex flex-grow items-center justify-start">
                    <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.REGULAR} className="mr-8">
                      Press {optionIndex + 1} for
                    </Text>
                    <TextFieldFormik
                      id={`${FORM_IDS.QUESTIONS}.${questionIndex}.answer_options.${optionIndex}.content_configuration.message_text`}
                      label={`Option ${optionIndex + 1}`}
                    />
                  </div>

                  {!isEmpty(question?.answer_options) ? (
                    <IconButton
                      aria-label="delete option"
                      onClick={() => {
                        removeOption(optionIndex);
                        // Update positions of remaining options
                        const updatedOptions = [...(question?.answer_options || [])];
                        updatedOptions.splice(optionIndex, 1);
                        updatedOptions.forEach((opt, idx) => {
                          setFieldValue(
                            `${FORM_IDS.QUESTIONS}.${questionIndex}.answer_options.${idx}.position`,
                            idx + 1
                          );
                        });
                      }}
                      size="small"
                      className="ml-4"
                    >
                      <TrashIcon />
                    </IconButton>
                  ) : null}
                </div>
              )
            )}

            {isEmpty(question?.answer_options) || (question?.answer_options && question?.answer_options?.length < 5) ? (
              <FsButton
                color={FsButton.COLORS.primary}
                onClick={() => {
                  const nextDigit = Math.max(...(question?.answer_options?.map((o) => o.position || 0) || []), 0) + 1;
                  pushOption(getEmptyOption(Math.min(nextDigit, 9)));
                }}
              >
                <AddIcon />
                Add Answer Option
              </FsButton>
            ) : null}
          </>
        )}
      </FieldArray>
    </div>
  );
};

export default OptionsConfiguration;
